import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { selectionService, Selection } from '../../services/selection';
import moment from 'moment-jalaali';
import { ShoppingBag } from 'lucide-react';

const formatPrice = (price: string | number) => {
  return Number(price).toLocaleString('fa-IR');
};

const SelectionSharePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchSelection(id);
    }
  }, [id]);

  const fetchSelection = async (selectionId: string) => {
    try {
      const response = await selectionService.getById(selectionId);
      setSelection(response.data.data);
    } catch (error) {
      console.error('Error fetching selection:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!selection) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">لیست یافت نشد</p>
      </div>
    );
  }

  const calculateTotals = () => {
    if (!selection.items) return { count: 0, weight: 0, price: 0 };
    
    return selection.items.reduce((acc, item) => {
      const price = (item.product as any)?.calculated_price?.total_price || 0;
      const weight = item.product?.weight || 0;
      
      return {
        count: acc.count + 1,
        weight: acc.weight + weight,
        price: acc.price + price
      };
    }, { count: 0, weight: 0, price: 0 });
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
      <div 
        id="product-card-share" 
        className="bg-white rounded-3xl shadow-xl overflow-hidden max-w-2xl w-full border border-gray-100"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[--color-primary] to-[--color-primary-light] text-white p-8 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h1 className="text-3xl font-bold mb-2">لیست منتخب من</h1>
            <p className="opacity-90">راز گلد - انتخابی هوشمندانه</p>
          </div>
          <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('/bg-pattern.png')] bg-repeat" />
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="space-y-4 mb-8">
            {selection.items.map((item) => (
              <div key={item.product_id} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                  {item.product?.image_urls?.[0] ? (
                    <img
                      src={item.product.image_urls[0]}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <ShoppingBag className="w-8 h-8" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1">{item.product?.name}</h3>
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>کد: {item.product?.code}</span>
                    <span>{item.product?.weight} گرم</span>
                  </div>
                  <div className="text-accent font-bold mt-2 text-left">
                    {(item.product as any)?.calculated_price ? formatPrice((item.product as any).calculated_price.total_price) : '---'} تومان
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer / Totals */}
          <div className="bg-[--color-primary] bg-opacity-5 rounded-xl p-6 border border-[--color-primary] border-opacity-10">
            <div className="grid grid-cols-3 gap-4 text-center divide-x divide-x-reverse divide-gray-200">
              <div>
                <div className="text-gray-500 text-sm mb-1">تعداد اقلام</div>
                <div className="font-bold text-gray-900">{totals.count}</div>
              </div>
              <div>
                <div className="text-gray-500 text-sm mb-1">وزن کل</div>
                <div className="font-bold text-gray-900">{totals.weight.toFixed(3)} گرم</div>
              </div>
              <div>
                <div className="text-gray-500 text-sm mb-1">جمع کل</div>
                <div className="font-bold text-accent">{formatPrice(totals.price)} تومان</div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-gray-400 text-sm flex items-center justify-center gap-2">
            <span>تاریخ ایجاد: {moment(selection.created_at).format('jYYYY/jMM/jDD')}</span>
            <span>•</span>
            <span>raazgold.com</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectionSharePage;
